import { normalizeScopeDateContext } from "../scopeDate.js";

function parseExpenseDate(dateText) {
  if (!dateText) {
    return null;
  }

  const parsed = new Date(`${dateText}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function compareByTime(left, right) {
  return left.getTime() - right.getTime();
}

function sumExpenseAmounts(expenseRows = []) {
  return expenseRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
}

function getExpenseMonthIndex(row) {
  const parsedDate = parseExpenseDate(row.date);
  if (!parsedDate) {
    return null;
  }

  return parsedDate.getMonth();
}

function getParsedExpenseDates(expenseRows = []) {
  return expenseRows
    .map((row) => parseExpenseDate(row.date))
    .filter(Boolean)
    .sort(compareByTime);
}

function isOnOrBeforeToday(parsedDate, dateContext) {
  if (!dateContext.isCurrentYear) {
    return true;
  }

  if (parsedDate.getFullYear() !== dateContext.scopedYear) {
    return false;
  }

  if (parsedDate.getMonth() < dateContext.currentMonthIndex) {
    return true;
  }

  if (parsedDate.getMonth() > dateContext.currentMonthIndex) {
    return false;
  }

  return parsedDate.getDate() <= dateContext.currentDate;
}

function isInCurrentMonthOrEarlier(parsedDate, dateContext) {
  if (!dateContext.isCurrentYear) {
    return true;
  }

  if (parsedDate.getFullYear() !== dateContext.scopedYear) {
    return false;
  }

  return parsedDate.getMonth() <= dateContext.currentMonthIndex;
}

export function filterExpenseRowsThroughToday(expenseRows = [], dateContext) {
  return expenseRows.filter((row) => {
    const parsedDate = parseExpenseDate(row.date);
    return parsedDate ? isOnOrBeforeToday(parsedDate, dateContext) : false;
  });
}

export function filterExpenseRowsThroughCurrentMonth(expenseRows = [], dateContext) {
  return expenseRows.filter((row) => {
    const parsedDate = parseExpenseDate(row.date);
    return parsedDate ? isInCurrentMonthOrEarlier(parsedDate, dateContext) : false;
  });
}

function isCurrentMonthDate(parsedDate, dateContext) {
  return parsedDate.getFullYear() === dateContext.scopedYear
    && parsedDate.getMonth() === dateContext.currentMonthIndex;
}

function splitExpenseRowsByMonthState(expenseRows = [], dateContext) {
  const filteredRows = filterExpenseRowsThroughCurrentMonth(expenseRows, dateContext);

  return filteredRows.reduce((groups, row) => {
    const parsedDate = parseExpenseDate(row.date);
    if (!parsedDate) {
      return groups;
    }

    if (dateContext.isCurrentYear && isCurrentMonthDate(parsedDate, dateContext)) {
      groups.current.push(row);
      return groups;
    }

    groups.closed.push(row);
    return groups;
  }, {
    closed: [],
    current: []
  });
}

function getCompletedMonthCount(dateContext, expenseRows = []) {
  const parsedDates = getParsedExpenseDates(expenseRows);

  if (parsedDates.length === 0) {
    return 0;
  }

  const firstExpenseDate = parsedDates[0];
  const startMonthIndex = firstExpenseDate.getMonth();

  if (!dateContext.isCurrentYear) {
    return Math.max(12 - startMonthIndex, 1);
  }

  return Math.max(0, dateContext.currentMonthIndex - startMonthIndex);
}

export function inspectMonthlyImpliedSpend(expenseRows = [], dateInput) {
  const dateContext = normalizeScopeDateContext(dateInput);
  const parsedDates = getParsedExpenseDates(expenseRows);
  const currentMonthOrEarlierRows = filterExpenseRowsThroughCurrentMonth(expenseRows, dateContext);
  const totalSpent = sumExpenseAmounts(currentMonthOrEarlierRows);
  const { closed: closedMonthExpenseRows, current: currentMonthRows } = splitExpenseRowsByMonthState(expenseRows, dateContext);
  const currentMonthSpent = sumExpenseAmounts(currentMonthRows);
  const spentBeforeCurrentMonth = sumExpenseAmounts(closedMonthExpenseRows);
  const completedMonths = getCompletedMonthCount(dateContext, expenseRows);
  const closedOccurrenceCount = closedMonthExpenseRows.length;
  const currentMonthCount = currentMonthRows.length;
  const averageMonthlyOccurrences = completedMonths > 0 ? (closedOccurrenceCount / completedMonths) : 0;
  const averageSpendPerOccurrence = closedOccurrenceCount > 0 ? (spentBeforeCurrentMonth / closedOccurrenceCount) : 0;
  const remainingExpectedOccurrences = Math.max(averageMonthlyOccurrences - currentMonthCount, 0);
  const structuralImplied = currentMonthSpent + (remainingExpectedOccurrences * averageSpendPerOccurrence);
  const implied = expenseRows.length > 0 ? structuralImplied : 0;
  const firstExpenseDate = parsedDates[0] ?? null;

  return {
    implied,
    totalSpent,
    spentBeforeCurrentMonth,
    currentMonthSpent,
    structuralImplied,
    firstExpenseDate,
    expenseCount: expenseRows.length,
    closedOccurrenceCount,
    currentMonthCount,
    averageMonthlyOccurrences,
    averageSpendPerOccurrence,
    remainingExpectedOccurrences,
    parsedDates,
    dateContext,
    completedMonths,
    explanation: [
      `1. Total ongoing spent in this category: ${totalSpent}.`,
      `2. Closed-month spend = ${spentBeforeCurrentMonth} across ${closedOccurrenceCount} closed-month occurrences.`,
      `3. Completed months since the first expense month = ${completedMonths}, so average monthly occurrences = ${averageMonthlyOccurrences}.`,
      `4. Average spend per occurrence = ${averageSpendPerOccurrence}.`,
      `5. Current month has ${currentMonthCount} occurrences and ${currentMonthSpent} spent so far.`,
      `6. Remaining expected occurrences = max(average monthly occurrences - current month count, 0) = ${remainingExpectedOccurrences}.`,
      `7. Final implied = current month spent + remaining expected occurrences * average spend per occurrence = ${implied}.`
    ]
  };
}

export function calculateMonthlyImpliedSpend(expenseRows = [], dateInput) {
  return inspectMonthlyImpliedSpend(expenseRows, dateInput).implied;
}

export function calculateProjectedAnnualSpend(expenseRows = [], dateInput) {
  return inspectRecurringProjectionByMonth(expenseRows, dateInput).projectedAnnualTotal;
}

export function inspectRecurringProjectionByMonth(expenseRows = [], dateInput) {
  const dateContext = normalizeScopeDateContext(dateInput);
  const expenseRowsByMonth = Array.from({ length: 12 }, (_, monthIndex) => (
    expenseRows.filter((row) => getExpenseMonthIndex(row) === monthIndex)
  ));

  if (!expenseRows.length) {
    return {
      dateContext,
      currentSnapshot: inspectMonthlyImpliedSpend([], dateContext),
      projectedAnnualTotal: 0,
      months: Array.from({ length: 12 }, (_, monthIndex) => ({
        monthIndex,
        monthCount: 0,
        monthSpend: 0,
        recurringEstimate: 0,
        averageMonthlyOccurrences: 0,
        averageSpendPerOccurrence: 0,
        remainingExpectedOccurrences: 0,
        recurringBaseline: 0,
        projectedContribution: 0
      }))
    };
  }

  if (!dateContext.isCurrentYear) {
    const monthTotals = expenseRowsByMonth.map((monthRows, monthIndex) => {
      const monthSpend = sumExpenseAmounts(monthRows);
      const cumulativeRows = expenseRowsByMonth.slice(0, monthIndex + 1).flat();
      const cumulativeSpend = sumExpenseAmounts(cumulativeRows);
      const cumulativeCount = cumulativeRows.length;
      const activeMonths = expenseRowsByMonth
        .slice(0, monthIndex + 1)
        .filter((rows) => rows.length > 0)
        .length;
      const averageSpendPerOccurrence = cumulativeCount > 0 ? (cumulativeSpend / cumulativeCount) : 0;
      const averageMonthlyOccurrences = activeMonths > 0 ? (cumulativeCount / activeMonths) : 0;
      const remainingExpectedOccurrences = Math.max(averageMonthlyOccurrences - monthRows.length, 0);
      const recurringEstimate = monthSpend + (remainingExpectedOccurrences * averageSpendPerOccurrence);
      const recurringBaseline = monthRows.length < averageMonthlyOccurrences
        ? Math.max(recurringEstimate, monthSpend, 0)
        : Math.max(monthSpend, 0);

      return {
        monthIndex,
        monthCount: monthRows.length,
        monthSpend,
        recurringEstimate,
        averageMonthlyOccurrences,
        averageSpendPerOccurrence,
        remainingExpectedOccurrences,
        recurringBaseline,
        projectedContribution: monthSpend
      };
    });

    return {
      dateContext,
      currentSnapshot: inspectMonthlyImpliedSpend(expenseRows, dateContext),
      projectedAnnualTotal: sumExpenseAmounts(expenseRows),
      months: monthTotals
    };
  }

  const currentSnapshotRows = filterExpenseRowsThroughCurrentMonth(expenseRows, dateContext);
  const currentSnapshot = inspectMonthlyImpliedSpend(currentSnapshotRows, dateContext);
  const historicalRows = expenseRowsByMonth.slice(0, dateContext.currentMonthIndex).flat();
  const historicalMonthCount = Math.max(dateContext.currentMonthIndex, 0);
  const historicalOccurrenceCount = historicalRows.length;
  const historicalAverageMonthlyOccurrences = historicalMonthCount > 0
    ? (historicalOccurrenceCount / historicalMonthCount)
    : 0;
  const months = expenseRowsByMonth.map((monthRows, monthIndex) => {
    const monthCount = monthRows.length;
    const monthSpend = sumExpenseAmounts(monthRows);
    const cumulativeRows = expenseRowsByMonth.slice(0, monthIndex + 1).flat();
    const cumulativeSpend = sumExpenseAmounts(cumulativeRows);
    const cumulativeCount = cumulativeRows.length;
    const averageSpendPerOccurrence = cumulativeCount > 0 ? (cumulativeSpend / cumulativeCount) : 0;
    const averageMonthlyOccurrences = monthIndex < dateContext.currentMonthIndex
      ? monthCount
      : Math.max(monthCount, historicalAverageMonthlyOccurrences);
    const remainingExpectedOccurrences = Math.max(averageMonthlyOccurrences - monthCount, 0);
    const recurringEstimate = monthSpend + (remainingExpectedOccurrences * averageSpendPerOccurrence);
    const recurringBaseline = monthIndex < dateContext.currentMonthIndex
      ? monthSpend
      : monthCount < historicalAverageMonthlyOccurrences
        ? Math.max(recurringEstimate, monthSpend, 0)
        : Math.max(monthSpend, 0);
    const projectedContribution = monthIndex < dateContext.currentMonthIndex
      ? monthSpend
      : recurringBaseline;

    return {
      monthIndex,
      monthCount,
      monthSpend,
      recurringEstimate,
      averageMonthlyOccurrences,
      averageSpendPerOccurrence,
      remainingExpectedOccurrences,
      recurringBaseline,
      projectedContribution
    };
  });

  return {
    dateContext,
    currentSnapshot,
    projectedAnnualTotal: months.reduce((sum, month) => sum + month.projectedContribution, 0),
    months
  };
}
